import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../@shared/firebase/config"; // Update path if needed
import CreateTestAccountModal from "../components/CreateTestAccountModal";
import EditUserModal from "../components/admin/EditUserModal";
import { doc, updateDoc, writeBatch, deleteDoc } from "firebase/firestore";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { MainContext } from "../App";
import { useCardStore } from "../@shared/store/useCardsStore";
import AddRewardModal from "../components/admin/AddRewardModal";
import CustomTabBar from "../components/playerDashboard/CustomTabBar";
import CreateItemModal from "../components/admin/CreateItemModal";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && (
        <Box p={3}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const dummyData = {
  users: [
    { id: 1, name: "Antony Devon", email: "admin@game.com", role: "admin" },
    { id: 2, name: "Toshi Blaze", email: "toshi@game.com", role: "player" },
  ],
  characters: [
    { id: 1, name: "Zaporia", power: 90, element: "Fire" },
    { id: 2, name: "Tenshi", power: 70, element: "Wind" },
  ],
  infinityCards: [
    { id: 1, name: "Archer’s Charm", type: "Buff", effect: "+10 Accuracy" },
    { id: 2, name: "Frost Fang", type: "Attack", effect: "Freeze" },
  ],
  quests: [
    { id: 1, title: "Defeat 3 Enemies", reward: "100 XP", type: "Daily" },
    { id: 2, title: "Win 5 Matches", reward: "150 XP", type: "Weekly" },
  ],
};

export default function AdminScreen() {
  const { userData } = useAuthStore();
  const { characterCards, infinityCards } = useCardStore();
  const { navigate } = useContext(MainContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [cosmeticsTab, setCosmeticsTab] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [editingUserData, setEditingUserData] = useState(null);
  const [addingRewardsData, setAddingRewardData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [allCharacterCards, setAllCharacterCards] = useState([]);

  const [allAvatars, setAllAvatars] = useState(null);
  const [allSkins, setAllSkins] = useState(null);
  const [allStickers, setAllStickers] = useState(null);
  const [allEmotes, setAllEmotes] = useState(null);
  const [allScreens, , setAllScreens] = useState(null);

  const [createItemModalOpen, setCreateItemModalOpen] = useState(false);
  const [deleteContext, setDeleteContext] = useState({ type: "", label: "" });

  const handleCreateNewItemModalOpen = () => {
    setCreateItemModalOpen(true);
  };

  const getCollectionName = () => {
    switch (tabIndex) {
      case 2:
        return "Character cards";
      case 3:
        return "Infinity cards";
      case 5:
        return ["avatars", "skins", "stickers", "emotes", "screens"][
          cosmeticsTab
        ];
      default:
        return null;
    }
  };

  const fetchDataForTab = () => {
    switch (tabIndex) {
      case 2:
        return fetchAllCharacterCards();
      case 3:
        return;
      case 5:
        switch (cosmeticsTab) {
          case 0:
            return fetchAllAvatars();
          case 1:
            return fetchAllSkins();
          case 2:
            return fetchAllStickers();
          case 3:
            return fetchAllEmotes();
          case 4:
            return fetchAllScreens();
        }
    }
  };

  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(userList);
    } catch (err) {
      console.error("🔥 Failed to fetch users:", err);
    }
  };
  const fetchWaitlist = async () => {
    try {
      const snapshot = await getDocs(collection(db, "waitlist"));
      const emailList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setWaitlist(emailList);
    } catch (err) {
      console.error("🔥 Failed to fetch users:", err);
    }
  };
  const fetchAllCharacterCards = async () => {
    try {
      const snapshot = await getDocs(collection(db, "Character cards"));
      const cards = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllCharacterCards(cards);
    } catch (err) {
      console.error("🔥 Failed to fetch character cards:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAvatars = async () => {
    try {
      const snapshot = await getDocs(collection(db, "avatars"));
      const avatarDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllAvatars(avatarDocs);
    } catch (err) {
      console.error("🔥 Failed to fetch avatars:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchAllSkins = async () => {
    try {
      const snapshot = await getDocs(collection(db, "skins"));
      const skinDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllSkins(skinDocs);
    } catch (err) {
      console.error("🔥 Failed to fetch skins:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchAllStickers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "stickers"));
      const stickerDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllStickers(stickerDocs);
    } catch (err) {
      console.error("🔥 Failed to fetch stickers:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchAllEmotes = async () => {
    try {
      const snapshot = await getDocs(collection(db, "emotes"));
      const emoteDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllEmotes(emoteDocs);
    } catch (err) {
      console.error("🔥 Failed to fetch emotes:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchAllScreens = async () => {
    try {
      const snapshot = await getDocs(collection(db, "screens"));
      const screenDocs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllScreens(screenDocs);
    } catch (err) {
      console.error("🔥 Failed to fetch screens:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData.roles.includes("admin")) {
      fetchUsers();
      fetchWaitlist();
      fetchAllCharacterCards();
    } else {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    fetchDataForTab();
  }, [tabIndex, cosmeticsTab]);

  const handleBulkRewards = async () => {
    for (const userId of selectedUserIds) {
      await issueInitialSignupRewards(userId);
    }
  };

  const issueInitialSignupRewards = async (userId) => {
    try {
      console.log("🎁 Issuing signup rewards for:", userId);
      const rewardsRef = collection(db, "users", userId, "rewards");
      // Fetch available character cards
      const cCards = [...characterCards]
        .map((card) => ({
          ...card,
          type: "character",
        }))
        .filter(
          (card) =>
            !!card.isProviderStripeId ||
            card.name === "Toshi" ||
            card.name === "Ashro"
        );

      // Fetch available infinity cards
      let iCards = [...infinityCards].filter((card) => card.finite !== "TRUE");

      // Defensive check
      if (cCards?.length === 0 || iCards?.length < 40) {
        console.warn("⚠️ Not enough cards to issue signup rewards.");
        return;
      }

      // Shuffle helper
      const shuffleArray = (arr) => {
        const shuffled = [...arr];
        for (let i = shuffled?.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      // 🎴 Shuffle and slice to 40 unique cards
      const shuffledInfinity = shuffleArray(iCards).slice(0, 40);

      // 🃏 Break into 4 card packs (10 each)
      const cardPacks = [];
      for (let i = 0; i < 4; i++) {
        const pack = shuffledInfinity.slice(i * 10, i * 10 + 10);
        cardPacks.push(pack);
      }

      // 1. Character card reward
      const getRandom = (arr) => arr[Math.floor(Math.random() * arr?.length)];
      await addDoc(rewardsRef, {
        type: "character card",
        issuedAt: Date.now(),
        claimed: false,
        reward: getRandom(cCards),
        amount: 1,
      });

      // 2-5. Four card pack rewards
      for (let i = 0; i < 4; i++) {
        await addDoc(rewardsRef, {
          type: "card pack",
          issuedAt: Date.now(),
          claimed: false,
          reward: {
            pack: {
              cards: cardPacks[i],
            },
          },
          amount: 1,
        });
      }

      // 6. Infinity tokens reward
      await addDoc(rewardsRef, {
        type: "infinity tokens",
        issuedAt: Date.now(),
        claimed: false,
        reward: { currency: "Infinity Tokens" },
        amount: 250,
      });

      console.log("✅ Signup rewards issued for user:", userId);
    } catch (e) {
      console.error("Error in issueInitialSignupRewards:", e);
    }
  };

  //   const renderTable = (rows, columns, loading = false, type = "") => (
  //     <>
  //       <Box
  //         sx={{
  //           display: "flex",
  //           justifyContent: "space-between",
  //           alignItems: "center",
  //           mb: 2,
  //           flexWrap: "wrap",
  //           gap: 2,
  //         }}
  //       >
  //         <TextField
  //           label={`Search ${getSectionLabel()}`}
  //           value={searchQuery}
  //           onChange={(e) => setSearchQuery(e.target.value)}
  //           size="small"
  //           sx={{ width: "100%", maxWidth: "70%" }}
  //         />

  //         <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
  //           <Button
  //             variant="contained"
  //             color="primary"
  //             startIcon={<Add />}
  //             onClick={handleCreateNewItemModalOpen}
  //           >
  //             Create New
  //           </Button>
  //           {tabIndex === 0 && (
  //             <Button onClick={() => setModalOpen(true)} variant="outlined">
  //               Create Test Account
  //             </Button>
  //           )}
  //           <Typography
  //             variant="body2"
  //             color="text.secondary"
  //             style={{ fontWeight: "bold" }}
  //           >
  //             {loading ? "Loading..." : "Amount: "}
  //             {type === "users"
  //               ? users?.length
  //               : type === "waitlist"
  //               ? waitlist?.length
  //               : type === "characterCards"
  //               ? allCharacterCards?.length
  //               : type === "infinityCards"
  //               ? infinityCards?.length
  //               : type === "avatars"
  //               ? allAvatars?.length
  //               : type === "skins"
  //               ? allSkins?.length
  //               : type === "stickers"
  //               ? allStickers?.length
  //               : type === "emotes"
  //               ? allEmotes?.length
  //               : type === "screens"
  //               ? allScreens?.length
  //               : 0}
  //           </Typography>
  //         </Box>
  //       </Box>

  //       <TableContainer
  //         component={Paper}
  //         sx={{
  //           maxHeight: "75dvh",
  //           overflowY: "auto",
  //         }}
  //       >
  //         <Table stickyHeader>
  //           <TableHead>
  //             <TableRow>
  //               {type === "users" && (
  //                 <TableCell padding="checkbox">
  //                   <input
  //                     type="checkbox"
  //                     checked={
  //                       users?.length > 0 &&
  //                       selectedUserIds?.length === users?.length
  //                     }
  //                     onChange={(e) => {
  //                       if (e.target.checked) {
  //                         setSelectedUserIds(users.map((u) => u.id));
  //                       } else {
  //                         setSelectedUserIds([]);
  //                       }
  //                     }}
  //                   />
  //                 </TableCell>
  //               )}
  //               {columns.map((col) => (
  //                 <TableCell key={col} style={{ fontWeight: "bold" }}>
  //                   {col}
  //                 </TableCell>
  //               ))}

  //               <TableCell style={{ fontWeight: "bold" }}>
  //                 {selectedUserIds?.length > 1 ? (
  //                   <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //                     <Typography variant="body2" fontWeight={500}>
  //                       {selectedUserIds?.length} selected
  //                     </Typography>
  //                     <IconButton
  //                       color="primary"
  //                       title="Bulk Edit"
  //                       onClick={() => {
  //                         setEditingUserData(null); // null triggers bulk mode
  //                         setEditModalOpen(true);
  //                       }}
  //                     >
  //                       <Edit />
  //                     </IconButton>
  //                     <IconButton
  //                       color="error"
  //                       title="Bulk Delete"
  //                       onClick={() => {
  //                         handleBulkRewards();
  //                       }}
  //                     >
  //                       <Delete />
  //                     </IconButton>
  //                   </Box>
  //                 ) : (
  //                   "Actions"
  //                 )}
  //               </TableCell>
  //             </TableRow>
  //           </TableHead>

  //           <TableBody>
  //             {loading ? (
  //               <TableRow>
  //                 <TableCell colSpan={columns?.length + 1}>Loading...</TableCell>
  //               </TableRow>
  //             ) : (
  //               rows.map((row, i) => (
  //                 <TableRow key={i} hover>
  //                   {type === "users" && (
  //                     <TableCell padding="checkbox">
  //                       <input
  //                         type="checkbox"
  //                         checked={selectedUserIds.includes(row.ID)}
  //                         onChange={(e) => {
  //                           const checked = e.target.checked;
  //                           const id = row.ID;
  //                           if (checked) {
  //                             setSelectedUserIds((prev) => [...prev, id]);
  //                           } else {
  //                             setSelectedUserIds((prev) =>
  //                               prev.filter((uid) => uid !== id)
  //                             );
  //                           }
  //                         }}
  //                       />
  //                     </TableCell>
  //                   )}
  //                   {columns.map((col) => {
  //                     if (col === "ID") {
  //                       const user = users.find((u) => u.id === row.ID);

  //                       return (
  //                         <TableCell key={col}>
  //                           <Box
  //                             sx={{ display: "flex", flexDirection: "column" }}
  //                           >
  //                             <Typography>{row[col]}</Typography>
  //                             {tabIndex === 0 && (
  //                               <Box
  //                                 sx={{
  //                                   display: "flex",
  //                                   flexWrap: "wrap",
  //                                   gap: 0.5,
  //                                   mt: 0.5,
  //                                 }}
  //                               >
  //                                 {!user?.emailVerified ? (
  //                                   <Chip
  //                                     size="small"
  //                                     color="warning"
  //                                     label="Not Verified"
  //                                   />
  //                                 ) : (
  //                                   <Chip
  //                                     size="small"
  //                                     color="success"
  //                                     label="Verified"
  //                                   />
  //                                 )}
  //                                 {user?.onboardingComplete && (
  //                                   <Chip
  //                                     size="small"
  //                                     color="info"
  //                                     label="Onboarded"
  //                                   />
  //                                 )}
  //                                 {user?.roles?.includes("ip_provider") &&
  //                                 !user?.stripeId ? (
  //                                   <Chip
  //                                     size="small"
  //                                     color="error"
  //                                     label="No Stripe ID"
  //                                   />
  //                                 ) : user?.stripeId ? (
  //                                   <Chip
  //                                     size="small"
  //                                     label={"SID: " + user?.stripeId}
  //                                   />
  //                                 ) : null}
  //                               </Box>
  //                             )}
  //                           </Box>
  //                         </TableCell>
  //                       );
  //                     } else if (col === "Image") {
  //                       return (
  //                         <TableCell key={col}>
  //                           <Box
  //                             sx={{
  //                               display: "flex",
  //                               flexDirection: "column",
  //                             }}
  //                           >
  //                             <Box
  //                               sx={{
  //                                 display: "flex",
  //                                 flexWrap: "wrap",
  //                                 gap: 0.5,
  //                                 mt: 0.5,
  //                               }}
  //                             >
  //                               {row.Image && (
  //                                 <img
  //                                   src={row.Image}
  //                                   alt="User Image"
  //                                   style={{
  //                                     width: "50px",
  //                                     height: "50px",
  //                                     objectFit: "contain",
  //                                   }}
  //                                 />
  //                               )}
  //                             </Box>
  //                           </Box>
  //                         </TableCell>
  //                       );
  //                     }

  //                     return <TableCell key={col}>{row[col]}</TableCell>;
  //                   })}

  //                   <TableCell>
  //                     <IconButton
  //                       color="primary"
  //                       disabled={selectedUserIds?.length > 1}
  //                       title="Edit"
  //                       onClick={() => {
  //                         setSelectedUserIds([row.ID]);
  //                         setEditingUserData(users.find((u) => u.id === row.ID));
  //                         setEditModalOpen(true);
  //                       }}
  //                     >
  //                       <Edit />
  //                     </IconButton>
  //                     <IconButton
  //                       color="error"
  //                       disabled={selectedUserIds?.length > 1}
  //                       title="Delete"
  //                       onClick={() => {
  //                         setSelectedUserIds([row.ID]);
  //                         const label =
  //                           row.Name || row.Title || row.Email || "this item";
  //                         setDeleteContext({ type, label });
  //                         setDeleteModalOpen(true);
  //                       }}
  //                     >
  //                       <Delete />
  //                     </IconButton>
  //                     {type === "users" && (
  //                       <IconButton
  //                         color="primary"
  //                         disabled={selectedUserIds?.length > 1}
  //                         title="Reward"
  //                         onClick={() => {
  //                           setSelectedUserIds([row.ID]);
  //                           setAddingRewardData(
  //                             users.find((u) => u.id === row.ID)
  //                           );
  //                           setRewardModalOpen(true);
  //                         }}
  //                       >
  //                         <img
  //                           src={require("../@shared/assets/icons/reward_icon.png")}
  //                           alt=""
  //                           style={{
  //                             height: 24,
  //                             width: 24,
  //                             objectFit: "contain",
  //                           }}
  //                         />
  //                       </IconButton>
  //                     )}
  //                   </TableCell>
  //                 </TableRow>
  //               ))
  //             )}
  //           </TableBody>
  //         </Table>
  //       </TableContainer>
  //     </>
  //   );

  const renderTable = (rows, columns, loading = false, type = "") => (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          label={`Search ${getSectionLabel()}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ width: "100%", maxWidth: "70%" }}
        />

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleCreateNewItemModalOpen}
          >
            Create New
          </Button>
          {tabIndex === 0 && (
            <Button onClick={() => setModalOpen(true)} variant="outlined">
              Create Test Account
            </Button>
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            style={{ fontWeight: "bold" }}
          >
            {loading ? "Loading..." : "Amount: "}
            {type === "users"
              ? users?.length
              : type === "waitlist"
              ? waitlist?.length
              : type === "characterCards"
              ? allCharacterCards?.length
              : type === "infinityCards"
              ? infinityCards?.length
              : type === "avatars"
              ? allAvatars?.length
              : type === "skins"
              ? allSkins?.length
              : type === "stickers"
              ? allStickers?.length
              : type === "emotes"
              ? allEmotes?.length
              : type === "screens"
              ? allScreens?.length
              : 0}
          </Typography>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "75dvh",
          overflowY: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {type === "users" && (
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={
                      users?.length > 0 &&
                      selectedUserIds?.length === users?.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUserIds(users.map((u) => u.id));
                      } else {
                        setSelectedUserIds([]);
                      }
                    }}
                  />
                </TableCell>
              )}
              {columns?.map((col) => (
                <TableCell key={col} style={{ fontWeight: "bold" }}>
                  {col}
                </TableCell>
              ))}
              <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns?.length + 1}>Loading...</TableCell>
              </TableRow>
            ) : (
              rows?.map((row, i) => (
                <TableRow key={i} hover>
                  {type === "users" && (
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(row.ID)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const id = row.ID;
                          if (checked) {
                            setSelectedUserIds((prev) => [...prev, id]);
                          } else {
                            setSelectedUserIds((prev) =>
                              prev.filter((uid) => uid !== id)
                            );
                          }
                        }}
                      />
                    </TableCell>
                  )}

                  {columns?.map((col) => {
                    // 🔍 Dynamic image preview for Image, WS, IS
                    if (["Image", "WS", "IS"].includes(col)) {
                      const src =
                        row[col] ||
                        row[col.toLowerCase()] ||
                        row.imageUrl ||
                        "";

                      return (
                        <TableCell key={col}>
                          {src && (
                            <img
                              src={src}
                              alt={`${col}`}
                              style={{
                                width: col === "Image" ? 50 : 32,
                                height: col === "Image" ? 50 : 32,
                                objectFit: "contain",
                                borderRadius: 4,
                                border: "1px solid #ccc",
                              }}
                            />
                          )}
                        </TableCell>
                      );
                    }

                    // Special user chip info
                    if (col === "ID" && type === "users") {
                      const user = users.find((u) => u.id === row.ID);
                      return (
                        <TableCell key={col}>
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography>{row[col]}</Typography>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              {!user?.emailVerified ? (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="Not Verified"
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  color="success"
                                  label="Verified"
                                />
                              )}
                              {user?.onboardingComplete && (
                                <Chip
                                  size="small"
                                  color="info"
                                  label="Onboarded"
                                />
                              )}
                              {user?.roles?.includes("ip_provider") &&
                              !user?.stripeId ? (
                                <Chip
                                  size="small"
                                  color="error"
                                  label="No Stripe ID"
                                />
                              ) : user?.stripeId ? (
                                <Chip
                                  size="small"
                                  label={"SID: " + user?.stripeId}
                                />
                              ) : null}
                            </Box>
                          </Box>
                        </TableCell>
                      );
                    }

                    // Default fallback
                    return <TableCell key={col}>{row[col]}</TableCell>;
                  })}

                  {/* === Actions === */}
                  <TableCell>
                    <IconButton
                      color="primary"
                      disabled={selectedUserIds?.length > 1}
                      title="Edit"
                      onClick={() => {
                        setSelectedUserIds([row.ID]);
                        if (type === "users") {
                          setEditingUserData(
                            users.find((u) => u.id === row.ID)
                          );
                          setEditModalOpen(true);
                        } else {
                          // Store selected item data for future EditItemModal
                          // You’ll implement this later
                        }
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      disabled={selectedUserIds?.length > 1}
                      title="Delete"
                      onClick={() => {
                        setSelectedUserIds([row.ID]);
                        const label =
                          row.Name ||
                          row.Title ||
                          row.Email ||
                          row.name ||
                          row.title ||
                          "item";
                        setDeleteContext({ type, label });
                        setDeleteModalOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                    {type === "users" && (
                      <IconButton
                        color="primary"
                        disabled={selectedUserIds?.length > 1}
                        title="Reward"
                        onClick={() => {
                          setSelectedUserIds([row.ID]);
                          setAddingRewardData(
                            users.find((u) => u.id === row.ID)
                          );
                          setRewardModalOpen(true);
                        }}
                      >
                        <img
                          src={require("../@shared/assets/icons/reward_icon.png")}
                          alt=""
                          style={{
                            height: 24,
                            width: 24,
                            objectFit: "contain",
                          }}
                        />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const getSectionLabel = () => {
    if (tabIndex === 0) return "Users";
    if (tabIndex === 1) return "Waitlist";
    if (tabIndex === 2) return "Character Cards";
    if (tabIndex === 3) return "Infinity Cards";
    if (tabIndex === 4) return "Quests";
    if (tabIndex === 5)
      return ["Avatars", "Skins", "Stickers", "Emotes", "Loading Screens"][
        cosmeticsTab
      ];
    return "";
  };

  const TABS = [
    "Users",
    "Waitlist",
    "Character Cards",
    "Infinity Cards",
    "Quests",
    "Cosmetics",
  ];

  const cTABS = ["Avatars", "Skins", "Stickers", "Emotes", "Loading Screens"];

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Admin Dashboard
      </Typography>

      <CustomTabBar tabs={TABS} activeIndex={tabIndex} onChange={setTabIndex} />

      {tabIndex === 5 && (
        <Box sx={{ width: "100%", mt: 1 }}>
          {/* <Typography variant="h5" fontWeight={700} gutterBottom>
            Select Cosmetic
          </Typography> */}
          <CustomTabBar
            tabs={cTABS}
            activeIndex={cosmeticsTab}
            onChange={setCosmeticsTab}
          />
        </Box>
      )}

      <TabPanel value={tabIndex} index={0}>
        {renderTable(
          users
            .filter(
              (u) =>
                u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.email?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((u) => ({
              ID: u.id,
              Username: u.username,
              Email: u.email,
              Roles: Array.isArray(u.roles) ? u.roles.join(", ") : "",
            })),
          ["ID", "Username", "Email", "Roles"],
          loading,
          "users"
        )}
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        {renderTable(
          waitlist
            .filter((u) =>
              u.email?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((u) => ({
              Email: u.email,
            })),
          ["Email"],
          loading,
          "waitlist"
        )}
      </TabPanel>
      <TabPanel value={tabIndex} index={2}>
        {renderTable(
          allCharacterCards
            .filter(
              (u) =>
                u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.ipProviderStripeId
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                u.collectionCreatorStripeId
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()) ||
                u.element?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((char) => ({
              ID: char.id,
              Image: char.imageUrl,
              Name: char.name,
              Element: char.element,
              IPPSID: char.ipProviderStripeId || "N/A",
              CCSID: char.collectionCreatorStripeId || "N/A",
            })),
          ["Image", "ID", "Name", "Element", "IPPSID", "CCSID"],
          loading,
          "characterCards"
        )}
      </TabPanel>
      <TabPanel value={tabIndex} index={3}>
        {renderTable(
          infinityCards
            .filter(
              (u) =>
                u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                u.effect?.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((card) => ({
              ID: card.id,
              Image: card.imageUrl,
              Name: card.name,
              Effect: card.effect,
              Finite: card.finite === "TRUE" ? "Yes" : "No",
            })),
          ["Image", "ID", "Name", "Effect", "Finite"],
          loading,
          "infinityCards"
        )}
      </TabPanel>
      <TabPanel value={tabIndex} index={4}>
        {renderTable(dummyData.quests, ["Title", "Reward", "Type"])}
      </TabPanel>
      <TabPanel value={tabIndex} index={5}>
        {{
          0: () =>
            renderTable(
              allAvatars
                ?.filter((a) =>
                  a.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((a) => ({
                  ID: a.id,
                  Name: a.name,
                  Image: a.image,
                  WS: a.ws,
                  IS: a.is,
                  tokenPrice: a.tokenPrice,
                  SkinName: a.skinName,
                })),
              ["Image", "WS", "IS", "ID", "Name", "tokenPrice", "SkinName"],
              loading,
              "avatars"
            ),
          1: () =>
            renderTable(
              allSkins
                ?.filter((s) =>
                  s.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((s) => ({
                  ID: s.id,
                  Name: s.name,
                  Image: s.image,
                  WS: s.ws,
                  IS: s.is,
                  tokenPrice: s.tokenPrice,
                  AvatarName: s.avatarName,
                })),
              ["Image", "WS", "IS", "ID", "Name", "tokenPrice", "AvatarName"],
              loading,
              "skins"
            ),
          2: () =>
            renderTable(
              allStickers
                ?.filter((s) =>
                  s.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((s) => ({
                  ID: s.id,
                  Name: s.name,
                  Image: s.image,
                  tokenPrice: s.tokenPrice,
                })),
              ["Image", "ID", "Name", "tokenPrice"],
              loading,
              "stickers"
            ),
          3: () =>
            renderTable(
              allEmotes
                ?.filter((e) =>
                  e.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((e) => ({
                  ID: e.id,
                  Name: e.name,
                  Image: e.image,
                  tokenPrice: e.tokenPrice,
                })),
              ["Image", "ID", "Name", "tokenPrice"],
              loading,
              "emotes"
            ),
          4: () =>
            renderTable(
              allScreens
                ?.filter((s) =>
                  s.name?.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((s) => ({
                  ID: s.id,
                  Name: s.name,
                  Image: s.image,
                  tokenPrice: s.tokenPrice,
                })),
              ["Image", "ID", "Name", "tokenPrice"],
              loading,
              "screens"
            ),
        }[cosmeticsTab]()}
      </TabPanel>

      <CreateTestAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      <EditUserModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        users={users}
        selectedUserIds={selectedUserIds}
        onConfirm={async (formData) => {
          try {
            if (selectedUserIds?.length === 1) {
              const userRef = doc(db, "users", selectedUserIds[0]);
              await updateDoc(userRef, {
                ...formData,
                updatedAt: new Date(),
              });
            } else {
              const batch = writeBatch(db);
              selectedUserIds.forEach((uid) => {
                const userRef = doc(db, "users", uid);
                batch.update(userRef, {
                  emailVerified: formData.emailVerified,
                  xp: formData.xp,
                  level: formData.level,
                  infinityTokens: formData.infinityTokens,
                  roles: formData.roles,
                  updatedAt: new Date(),
                });
              });
              await batch.commit();
            }

            console.log("✅ User(s) updated");

            // Optionally: refresh users
            const snapshot = await getDocs(collection(db, "users"));
            const userList = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setUsers(userList);
            setSelectedUserIds([]);
          } catch (err) {
            console.error("❌ Failed to update user(s):", err);
          } finally {
            setEditModalOpen(false);
          }
        }}
      />

      <AddRewardModal
        open={rewardModalOpen}
        onClose={() => setRewardModalOpen(false)}
        users={users}
        selectedUserIds={selectedUserIds}
        onConfirm={async (rewardData) => {
          try {
            for (const userId of selectedUserIds) {
              const rewardsRef = collection(db, "users", userId, "rewards");
              await addDoc(rewardsRef, rewardData);
            }

            console.log(
              "✅ Reward(s) issued to",
              selectedUserIds?.length,
              "user(s)"
            );
          } catch (err) {
            console.error("❌ Failed to issue reward(s):", err);
          } finally {
            setRewardModalOpen(false);
            setSelectedUserIds([]);
          }
        }}
      />

      <CreateItemModal
        open={createItemModalOpen}
        onClose={() => setCreateItemModalOpen(false)}
        collection={getCollectionName()}
        onCreate={async (itemData) => {
          try {
            const collectionName = getCollectionName();
            await addDoc(collection(db, collectionName), itemData);
            fetchDataForTab();
            setCreateItemModalOpen(false);
          } catch (err) {
            console.error("❌ Failed to create item:", err);
          }
        }}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        count={selectedUserIds?.length}
        onConfirm={async () => {
          try {
            if (deleteContext.type === "users") {
              if (selectedUserIds?.length === 1) {
                await deleteDoc(doc(db, "users", selectedUserIds[0]));
              } else {
                const batch = writeBatch(db);
                selectedUserIds.forEach((uid) =>
                  batch.delete(doc(db, "users", uid))
                );
                await batch.commit();
              }
              const snapshot = await getDocs(collection(db, "users"));
              const userList = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              setUsers(userList);
            } else {
              const collectionName = getCollectionName();
              if (selectedUserIds?.length === 1) {
                await deleteDoc(doc(db, collectionName, selectedUserIds[0]));
              } else {
                const batch = writeBatch(db);
                selectedUserIds.forEach((id) =>
                  batch.delete(doc(db, collectionName, id))
                );
                await batch.commit();
              }
              fetchDataForTab();
            }

            setSelectedUserIds([]);
          } catch (err) {
            console.error("❌ Failed to delete:", err);
          } finally {
            setDeleteModalOpen(false);
          }
        }}
        label={deleteContext.type}
      />
    </Box>
  );
}
